// 외부 링크 화면 공통 상태 화면·조회 훅 (WP5 4단계 §4.1·§4.3·§5.1)
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { EmptyState, ErrorState, IconInfoCircle, Skeleton } from '@indipia/ui-core';
import { classifyShareError, type ShareError, type ShareLinkViewErrorKind } from './errors';

// ForemanConfirmRespondPage 의 StateShell 과 같은 전체화면 중앙 정렬 (D-07 §14.2)
function Shell({ children }: { children: ReactNode }) {
  return <div className="flex h-screen w-full items-center justify-center bg-surface p-4">{children}</div>;
}

/** 로딩 스켈레톤 — 카드 2개 모양 (D-07 §3.2 #7) */
export function ShareLoadSkeleton() {
  return (
    <div className="flex min-h-screen w-full justify-center bg-surface p-4" aria-busy="true">
      <div className="flex w-full max-w-md flex-col gap-4">
        <Skeleton height="2rem" width="60%" />
        <Skeleton height="10rem" />
        <Skeleton height="8rem" />
      </div>
    </div>
  );
}

type ScreenKind = 'SESSION' | 'LINK' | 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'NETWORK' | 'SERVER' | 'UNKNOWN';

const COPY: Record<ScreenKind, { title: string; description: string }> = {
  SESSION: { title: '접속 시간이 지났어요', description: '카톡의 버튼을 다시 눌러 열어 주세요.' },
  LINK: {
    title: '열 수 없는 링크예요',
    description: '링크가 올바르지 않거나 더 이상 사용할 수 없어요. 요청한 곳으로 문의해 주세요.',
  },
  CLOSED: {
    title: '이미 마감된 요청이에요',
    description: '새로 온 요청이 있으면 그 알림톡의 버튼을 눌러 주세요.',
  },
  EXPIRED: {
    title: '만료된 링크예요',
    description: '입고일이 지나 더 이상 이용할 수 없어요. 문의는 요청한 곳으로 해 주세요.',
  },
  CHANGED: {
    title: '내용이 바뀌었어요',
    description: '내용이 바뀌어 이 링크로는 진행할 수 없어요. 새로 오는 알림톡의 버튼을 눌러 주세요.',
  },
  NETWORK: { title: '연결이 원활하지 않아요', description: '네트워크를 확인하고 다시 시도해 주세요.' },
  SERVER: { title: '잠시 후 다시 시도해 주세요', description: '문제가 계속되면 요청한 곳으로 문의해 주세요.' },
  UNKNOWN: { title: '화면을 표시하지 못했어요', description: '요청한 곳으로 문의해 주세요.' },
};

// 화면이 바뀌면 제목 영역으로 포커스를 옮긴다(h1 수준 제목 + tabIndex -1).
// ui-core 상태 블록의 제목은 h4 라, 렌더 뒤 그 요소를 h1 수준 제목으로 지정한다.
// 한계: ui-core EmptyState·ErrorState 는 이번 범위에서 수정할 수 없어(제목 레벨 prop 없음) 렌더 뒤 DOM 에 role·aria-level 을
// 덧씌우는 임시 처리이며, 해당 컴포넌트가 제목 레벨을 받게 되면 이 처리를 걷어낸다.
// 마크업이 바뀌어 '.state-block-text h4' 를 못 찾으면 포커스·제목 수준 지정이 조용히 빠진다.
function useFocusTitle(key: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = ref.current?.querySelector<HTMLElement>('.state-block-text h4');
    if (!h) return;
    h.setAttribute('role', 'heading');
    h.setAttribute('aria-level', '1');
    h.tabIndex = -1;
    h.focus({ preventScroll: true });
  }, [key]);
  return ref;
}

/** 전체 화면 상태. 재시도 버튼은 NETWORK·SERVER·UNKNOWN 이면서 onRetry 가 있을 때만 */
export function ShareStateScreen(p: { kind: ScreenKind; companyName?: string; onRetry?: () => void }) {
  const ref = useFocusTitle(p.kind);
  const copy = COPY[p.kind];
  let description = copy.description;
  if (p.kind === 'EXPIRED' && p.companyName) description += `\n문의: ${p.companyName}`;
  const isError = p.kind === 'NETWORK' || p.kind === 'SERVER' || p.kind === 'UNKNOWN';
  return (
    <Shell>
      <div ref={ref} style={{ whiteSpace: 'pre-line' }}>
        {isError ? (
          <ErrorState title={copy.title} description={description} onRetry={p.onRetry} />
        ) : (
          <EmptyState icon={<IconInfoCircle />} title={copy.title} description={description} />
        )}
      </div>
    </Shell>
  );
}

/** 화면별 전용 안내(입고 완료 등) */
export function ShareNoticeScreen(p: { icon?: ReactNode; title: string; description?: string; children?: ReactNode }) {
  const ref = useFocusTitle(p.title);
  return (
    <Shell>
      <div ref={ref} aria-live="polite">
        <EmptyState
          icon={p.icon ?? <IconInfoCircle />}
          title={p.title}
          description={p.description}
          action={p.children}
        />
      </div>
    </Shell>
  );
}

type ResourceState<T> = { status: 'loading' | 'ready' | 'error'; data?: T; error?: ShareError };

/** 마운트 시 load 실행. 언마운트·reload 시 이전 요청 abort(취소는 상태에 반영하지 않음) */
export function useShareResource<T>(load: (signal: AbortSignal) => Promise<T>): {
  status: 'loading' | 'ready' | 'error';
  data?: T;
  error?: ShareError;
  reload: () => void;
  setData: (d: T) => void;
} {
  const loadRef = useRef(load);
  loadRef.current = load;
  const ctrlRef = useRef<AbortController | null>(null);
  const [state, setState] = useState<ResourceState<T>>({ status: 'loading' });

  const run = useCallback(() => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setState({ status: 'loading' });
    let p: Promise<T>;
    try {
      p = loadRef.current(ctrl.signal);
    } catch (e) {
      p = Promise.reject(e);
    }
    p.then(
      (d) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', data: d });
      },
      (e: unknown) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'error', error: classifyShareError(e) });
      },
    );
  }, []);

  useEffect(() => {
    run();
    return () => ctrlRef.current?.abort();
  }, [run]);

  const setData = useCallback((d: T) => {
    ctrlRef.current?.abort();
    setState({ status: 'ready', data: d });
  }, []);

  return { status: state.status, data: state.data, error: state.error, reload: run, setData };
}

// ── 열람 링크 화면 오류 — 만료·회수·무효·일시 오류(+접속 시간 초과) ──
// 문구는 스케줄 열람 화면(ScheduleShareViewPage)과 같다. 분류는 errors.ts 의 classifyLinkViewError.
const LINK_VIEW_COPY: Record<ShareLinkViewErrorKind, { title: string; description: string }> = {
  EXPIRED: { title: '만료된 링크예요', description: '공유 기간이 끝나 더 이상 열 수 없어요. 새 링크가 필요하면 보낸 곳에 문의해 주세요.' },
  REVOKED: { title: '공유가 취소된 링크예요', description: '보낸 분이 공유를 취소했어요. 새 링크가 필요하면 보낸 곳에 문의해 주세요.' },
  INVALID: { title: '열 수 없는 링크예요', description: '링크가 올바르지 않아요. 받으신 알림톡의 버튼을 다시 눌러 보시고, 계속 열리지 않으면 보낸 곳에 문의해 주세요.' },
  TEMPORARY: { title: '연결이 원활하지 않아요', description: '네트워크를 확인하고 잠시 후 다시 시도해 주세요. 문제가 계속되면 보낸 곳에 문의해 주세요.' },
  SESSION: { title: '접속 시간이 지났어요', description: '카톡의 버튼을 다시 눌러 열어 주세요.' },
};

/** 열람 링크 오류 전체 화면. 재시도 버튼은 TEMPORARY 이면서 onRetry 가 있을 때만. title·description 으로 화면별 문구를 바꿀 수 있다 */
export function ShareLinkViewErrorScreen(p: {
  kind: ShareLinkViewErrorKind;
  onRetry?: () => void;
  title?: string;
  description?: string;
}) {
  const ref = useFocusTitle(p.kind + (p.title ?? ''));
  const copy = LINK_VIEW_COPY[p.kind];
  const title = p.title ?? copy.title;
  const description = p.description ?? copy.description;
  return (
    <Shell>
      <div ref={ref}>
        {p.kind === 'TEMPORARY' ? (
          <ErrorState title={title} description={description} onRetry={p.onRetry} />
        ) : (
          <EmptyState icon={<IconInfoCircle />} title={title} description={description} />
        )}
      </div>
    </Shell>
  );
}
